import { useCallback, useMemo, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';
import type { TableProps } from 'components/Table';
import dayjs from 'dayjs';
import useWorkout from 'hooks/workout/useWorkout';
import useWorkouts from 'hooks/workout/useWorkouts';
import useCreateWorkoutTemplateFromWorkout from 'hooks/workoutTemplate/useCreateWorkoutTemplateFromWorkout';
import { WorkoutTemplatePreviewData } from 'pages/private/owner/Workouts/Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview';
import { WorkoutList } from 'types';
import notificationHandler from 'utils/notifications';

import type { WorkoutColumnsInput } from './columns/useWorkoutsColumns';
import type { WorkoutTableContainerProps } from './types';
import { buildPreviewData } from './utils/buildPreviewData';
import { getOrdering } from './utils/getOrdering';

type TablePropsWithoutData = Omit<TableProps, 'tableData' | 'columns'>;

interface WorkoutTableContainerResult {
  tableData: WorkoutList[];
  columnsInput: WorkoutColumnsInput;
  tableProps: TablePropsWithoutData;
  deleteModal: {
    opened: boolean;
    workout: WorkoutList | null;
    onClose: () => void;
  };
  previewModal: {
    opened: boolean;
    workout: WorkoutTemplatePreviewData | null;
    onClose: () => void;
  };
}

export function useWorkoutTable({
  permission = true,
  search = '',
  kiloClassTypeId,
  sortOption = 'default',
  dateRange,
}: WorkoutTableContainerProps): WorkoutTableContainerResult {
  const [ascending, setAscending] = useState(true);

  const [workoutToDelete, setWorkoutToDelete] = useState<WorkoutList | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const [previewWorkoutId, setPreviewWorkoutId] = useState<string | null>(null);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);

  const ordering = useMemo(() => getOrdering(sortOption, ascending), [sortOption, ascending]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useWorkouts({
      search: search || undefined,
      ordering,
      kiloClassTypeId,
      limit: '20',
      start: dateRange?.[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
      end: dateRange?.[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
    });

  const createFromWorkoutMutation = useCreateWorkoutTemplateFromWorkout();

  const { successNotification, errorNotification } = notificationHandler();
  const { data: previewWorkoutData } = useWorkout(previewWorkoutId || undefined);

  const previewData: WorkoutTemplatePreviewData | null = useMemo(() => {
    if (!previewWorkoutData?.data) return null;
    return buildPreviewData(previewWorkoutData.data);
  }, [previewWorkoutData]);

  const tableData = useMemo(() => data?.pages.flatMap(page => page.data.results) ?? [], [data]);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      if (sortOption === 'name' && header === 'name') {
        setAscending(a => !a);
      }
    },
    [sortOption],
  );

  const handlePreview = useCallback(
    (workout: WorkoutList) => {
      setPreviewWorkoutId(workout.workoutId);
      openPreviewModal();
    },
    [openPreviewModal],
  );

  const handleClosePreview = useCallback(() => {
    closePreviewModal();
    setPreviewWorkoutId(null);
  }, [closePreviewModal]);

  const handleDelete = useCallback(
    (workout: WorkoutList) => {
      setWorkoutToDelete(workout);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setWorkoutToDelete(null);
    closeDeleteModal();
  }, [closeDeleteModal]);

  const handleSaveAsTemplate = useCallback(
    (workout: WorkoutList) => {
      createFromWorkoutMutation.mutate(workout.workoutId, {
        onSuccess: () => {
          successNotification(
            'Workout saved as template',
            `"${workout.name}" has been saved as a template successfully.`,
          );
        },
        onError: error => {
          const message = error.response?.data?.message ?? 'Failed to save workout as template';
          errorNotification('Failed to save workout as a template', message);
        },
      });
    },
    [createFromWorkoutMutation, successNotification, errorNotification],
  );

  return {
    tableData,
    columnsInput: {
      permission,
      handlePreview,
      handleSaveAsTemplate,
      handleDelete,
    },
    tableProps: {
      isSuccess,
      isLoading,
      isError,
      isFetchingNextPage,
      hasNextPage: !!hasNextPage,
      fetchNextPage,
      clickableHeaders: ['name'],
      handleHeaderClick,
      ordering: sortOption === 'name' ? 'name' : 'created_at',
      ascending,
      margin: 12,
      bgc: 'white' as const,
    },
    deleteModal: {
      opened: deleteModalOpened,
      workout: workoutToDelete,
      onClose: handleCloseDeleteModal,
    },
    previewModal: {
      opened: previewModalOpened,
      workout: previewData,
      onClose: handleClosePreview,
    },
  };
}
